import "can/construct/";
import { getAvailableProducts } from "../../helpers/products/products";

export default can.Construct.extend({
    init() {
        "use strict";

        this.returnPermissions = {};

        Vistracks.permissions.forEach((permission) => {
            const hasPermission = Vistracks.myUser.permissions
                .serialize()
                .find((userPermission) => {
                    return userPermission.name === permission.name;
                });

            if (hasPermission) {
                this.returnPermissions[permission.name] = true;
            }
        });

    },

    get() {
        "use strict";

        return this.returnPermissions;
    },

    isUserRoleAdmin() {
        "use strict";

        return this.returnPermissions.PERM_IS_ACCOUNT_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ROOT ||
            this.returnPermissions.PERM_IS_USER_ADMIN;
    },

    isUserRoleAssetAdmin() {
        "use strict";

        return this.returnPermissions.PERM_IS_ACCOUNT_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ROOT ||
            this.returnPermissions.PERM_EDIT_EQUIPMENT;
    },

    isUserRoleVTAdmin() {
        "use strict";

        return this.returnPermissions.PERM_IS_SYS_ADMIN;
    },

    isUserRoleAccountAdmin() {
        "use strict";

        return this.returnPermissions.PERM_IS_ACCOUNT_ADMIN;
    },

    canViewSubAccounts() {
        return this.returnPermissions.PERM_VIEW_SUB_ACCOUNTS;
    },

    canViewDefaultUserPreferences() {
        "use strict";

        return this.returnPermissions.PERM_IS_ACCOUNT_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ROOT;
    },

    canCreateSystemProperty() {
        "use strict";

        return this.returnPermissions.PERM_IS_SYS_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_ROOT;
    },

    isSystemUser() {
        "use strict";

        return this.returnPermissions.PERM_IS_SYS_ROOT ||
            this.returnPermissions.PERM_IS_SYS_ADMIN ||
            this.returnPermissions.PERM_IS_SYS_BILLING;
    },

    canCreateReseller() {
        "use strict";

        return Vistracks.myAccount.canCreateReseller;
    },

    isAzugaParentAccount() {
    "use strict";
        return Vistracks.myAccount && Vistracks.myAccount.accountTypeId === 1;
    },


    isReseller() {
        "use strict";

        return this.returnPermissions.PERM_IS_SYS_ADMIN ||
            Vistracks.myAccount.isReseller;
    },

    canReassignDrivingEvents() {
        "use strict";

        return (this.returnPermissions.PERM_IS_ACCOUNT_ADMIN && Vistracks.myAccount.isReseller) ||
            (this.returnPermissions.PERM_IS_ACCOUNT_ADMIN && Cookies.get("isPrimaryAccountReseller") === "true") ||
            (this.returnPermissions.PERM_IS_SYS_ADMIN || this.returnPermissions.PERM_IS_SYS_ROOT);
    },

    hasIftaPermission() {
        "use strict";
         return this.returnPermissions.PERM_VIEW_PORTAL_FUEL_RECEIPTS_TAB ||
         this.returnPermissions.PERM_VIEW_PORTAL_IFTA_REPORTS_TAB;
    },

    hasMobileWorkerPermission() {
        "use strict";

        return this.returnPermissions.PERM_VIEW_PORTAL_JOB_SITES_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_WORK_ORDERS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_WORK_ORDER_REPORTS_TAB;
    },

    hasDvirPermission() {
        "use strict";

        return this.returnPermissions.PERM_VIEW_PORTAL_DVIR_HISTORY_TAB ||
            this.returnPermissions.PERM_VIEW_DVIR_REPORTS;
    },

    hasHosPermission() {
        "use strict";

        return this.returnPermissions.PERM_VIEW_PORTAL_DRIVERS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_DRIVER_LOGS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_LOCATIONS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_VIOLATIONS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_UNIDENTIFIED_DRIVING_EVENTS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_HOS_REPORTS_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_DVIR_HISTORY_TAB ||
            this.returnPermissions.PERM_VIEW_PORTAL_MALFUNCTIONS_AND_DATA_DIAGNOSTICS ||
            this.returnPermissions.PERM_VIEW_PORTAL_LOG_EDITS;
    },




/**
 * Determines whether the current user has access to a specific route based on page and slug.
 *
 * This function checks the user's permissions and various application state flags to decide
 * if access should be granted for a given route. It supports complex permission logic for
 * different pages and slugs, including feature flags, hidden reports, and account types.
 *
 * @param {string} page - The main section or module of the application (e.g., 'hos', 'settings', 'ifta').
 * @param {string} slug - The specific route or tab within the page (e.g., 'drivers', 'logs', 'branding').
 * @returns {boolean} True if the user has access to the specified route, false otherwise.
 */

canAccessRoute(page, slug) {
    "use strict";
    const routePermissions = {
        hos: {
            drivers: "PERM_VIEW_PORTAL_DRIVERS_TAB",
            logs: "PERM_VIEW_PORTAL_DRIVER_LOGS_TAB",
            locations: "PERM_VIEW_PORTAL_LOCATIONS_TAB",
            violations: "PERM_VIEW_PORTAL_VIOLATIONS_TAB",
            "unidentified-driving": "PERM_VIEW_PORTAL_UNIDENTIFIED_DRIVING_EVENTS_TAB",
            "hos-reports": "PERM_VIEW_PORTAL_HOS_REPORTS_TAB",
            "eld-malfunctions": "PERM_VIEW_PORTAL_MALFUNCTIONS_AND_DATA_DIAGNOSTICS",
            "log-edits": "PERM_VIEW_PORTAL_LOG_EDITS",
            "data-transfer": "PERM_VIEW_PORTAL_HOS_REPORTS_TAB"
        },
        dvir: {
            "dvir-history": "PERM_VIEW_PORTAL_DVIR_HISTORY_TAB",
            reports: "PERM_VIEW_DVIR_REPORTS"
        },
        ifta: {
            "fuel-receipts": "PERM_VIEW_PORTAL_FUEL_RECEIPTS_TAB",
            reports: "PERM_VIEW_PORTAL_IFTA_REPORTS_TAB"
        },
        "mobile-worker": {
            jobsites: "PERM_VIEW_PORTAL_JOB_SITES_TAB",
            "work-orders": "PERM_VIEW_PORTAL_WORK_ORDERS_TAB",
            "mobile-worker-reports": "PERM_VIEW_PORTAL_WORK_ORDER_REPORTS_TAB"
        },
        settings: {
            equipment: "PERM_EDIT_EQUIPMENT",
            "driver-settings": "PERM_EDIT_DRIVERS",
            "driver-exceptions": "PERM_IS_ACCOUNT_ADMIN",
            terminals: "PERM_EDIT_TERMINALS",
            "dvir-form": "PERM_EDIT_DVIR_FORMS",
            "license-management": "PERM_IS_ACCOUNT_ADMIN",
            "accounts": "PERM_IS_ACCOUNT_ADMIN",
            "user-roles": "PERM_IS_ACCOUNT_ADMIN",
            "account-settings": "PERM_IS_ACCOUNT_ADMIN",
            "button-editor": "PERM_IS_ACCOUNT_ADMIN",
            branding: [
                "PERM_IS_ACCOUNT_ADMIN",
                "PERM_IS_SYS_ADMIN"
            ],
            "form-setup": [
                "PERM_IS_ACCOUNT_ADMIN",
                "PERM_VIEW_PORTAL_WORK_ORDER_REPORTS_TAB"
            ],
            "carrier-information": "PERM_EDIT_CARRIER_INFORMATION",
            alerts: [
                "PERM_IS_ACCOUNT_ADMIN",
                "PERM_EDIT_ALERTS"
            ],
            users: [
                "PERM_IS_ACCOUNT_ADMIN",
                "PERM_IS_SYS_ADMIN",
                "PERM_IS_SYS_ROOT",
                "PERM_IS_USER_ADMIN"
            ],
            "push-data": "PERM_IS_ACCOUNT_ADMIN",
            "push-notifications": "PERM_IS_ACCOUNT_ADMIN",
            "support-contact": "PERM_SUPPORT_CONTACT_TAB_VIEW",
            "upload-resources": "PERM_SUPPORT_CONTACT_TAB_VIEW",
            "scheduled-reports": null,
            "user-interface": null,
            "user-profile": null,
            "download-resources":  null
        },
        "admin-tools": {
            "data-transfer-admin": "PERM_IS_SYS_ADMIN",
            "module-management": "PERM_IS_SYS_ADMIN",
            "ifta-tax-rate": "PERM_IS_SYS_ADMIN",
            "billing-reports": "PERM_IS_SYS_ADMIN",
            "base-product-pricing": "PERM_IS_SYS_ADMIN",
            "aesi-monitor": "PERM_IS_SYS_ADMIN",
            "email-notifications": "PERM_IS_SYS_ADMIN",
            "reseller-keys": "PERM_IS_SYS_ADMIN",
            "app-release-notifications": "PERM_IS_SYS_ADMIN"
        }
    };

    const hasPermission = (perm) => {
        if (!perm) return true;
        if (Array.isArray(perm)) {
            return perm.some(p => !!this.returnPermissions[p]);
        }
        return !!this.returnPermissions[perm];
    };

    const appState = Vistracks.Scope && Vistracks.Scope.AppState;
    const SidebarVM = Vistracks.Scope && Vistracks.Scope.SidebarVM;
    const myAccount = Vistracks.myAccount;
    const hiddenReports = appState && appState.attr("hiddenReports") || [];
    const hasProducts = appState && appState.attr("hasProducts");
    const hasEldFeatures = appState && appState.attr("hasEldFeatures");
    const hasDvirFeatures = appState && appState.attr("hasDvirFeatures");
    const hasWorkOrderFeatures = appState && appState.attr("hasWorkOrderFeatures");
    const allowSubAccountBranding = SidebarVM && SidebarVM.attr("allowSubAccountBranding");
    const mobileWorker = appState && appState.attr("isMobileWorker");
    const isReseller = appState && appState.attr("isReseller");
    const hideFuelReceipts = appState && appState.attr("hideFuelReceipts");
    const hideLogShowEdits = appState && appState.attr("hideLogShowEdits");
    const hasHOSReports = SidebarVM && SidebarVM.attr("hasHOSReports");
    const hasIFTAReports = SidebarVM && SidebarVM.attr("hasIFTAReports");
    const hasWorkOrderReports = SidebarVM && SidebarVM.attr("hasWorkOrderReports");
    const licenseManagement = appState && appState.attr("licenseManagement");

    const availableProducts = myAccount && myAccount.features ? getAvailableProducts(myAccount.features) : [];

    const hasProduct = (featureName) => {
        return availableProducts.some(p => p.featureName === featureName);
    };
    let accountProducts = {};
    if (SidebarVM && SidebarVM.attr("products")) {
        accountProducts = SidebarVM.attr("products");
    } else if (myAccount && myAccount.products) {
        accountProducts = myAccount.products;
    }

    if (
        routePermissions[page] &&
        Object.prototype.hasOwnProperty.call(routePermissions[page], slug)
    ) {
        const perm = routePermissions[page][slug];

        if (page === "settings") {
            switch (slug) {
                case "accounts":
                    return (
                        isReseller &&
                        hasPermission(perm) &&
                        (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)
                    );
                case "account-settings":
                    return (
                        hasProducts &&
                        hasPermission(perm) &&
                        (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)
                    );
                case "carrier-information":
                    return (
                        hasPermission(perm) &&
                        (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)
                    );
                case "alerts":
                    return (
                        hasPermission(perm) &&
                        (hasEldFeatures || hasDvirFeatures)
                    );
                case "branding":
                    return (
                        hasProducts &&
                        allowSubAccountBranding &&
                        (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures) &&
                        hasPermission(perm)
                    );
                case "users":
                case "user-profile":
                    return (
                        hasProducts &&
                        (hasWorkOrderFeatures || hasDvirFeatures || hasEldFeatures)
                    );
                case "user-roles":
                    return (
                        hasPermission(perm) &&
                        (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures)
                    );
                case "form-setup":
                    return (
                        hasPermission(perm) &&
                        mobileWorker &&
                        hasWorkOrderFeatures
                    );
                case "user-interface":
                    return (
                        hasProducts &&
                        (hasEldFeatures || hasDvirFeatures || hasWorkOrderFeatures)
                    );
                case "license-management":
                    return !!licenseManagement;
                case "equipment":
                case "terminals":
                case "button-editor":
                    return (
                        hasPermission(perm) &&
                        hasProducts &&
                        (hasDvirFeatures || hasEldFeatures)
                    );
                case "driver-settings":
                case "driver-exceptions":
                    return (
                        hasPermission(perm) &&
                        hasProducts &&
                        (hasEldFeatures || accountProducts.dvirOnly)
                    );
                case "dvir-form":
                    return (
                        hasPermission(perm) &&
                        hasProducts &&
                        hasDvirFeatures
                    );
                case "push-data":
                case "push-notifications":
                case "support-contact":
                case "upload-resources":
                    return (
                        hasEldFeatures &&
                        hasPermission(perm)
                    );
                case "scheduled-reports":
                case "user-interface":
                case "user-profile":
                case "download-resources":
                    return true;
                default:
                    return hasPermission(perm);

            }
        }

        if (page === "hos") {
            if (!hasProduct("ELD")) return false;

            if (Array.isArray(hiddenReports) && hiddenReports.includes(slug)) return false;

            if (slug === "locations") {
                return hasPermission(perm) && hasEldFeatures && (myAccount && myAccount.features && myAccount.features.map);
            }
            if (slug === "hos-reports") {
                return hasPermission(perm) && hasHOSReports;
            }
            if (slug === "log-edits") {
                return hasPermission(perm) && !hideLogShowEdits;
            }
            return hasPermission(perm);
        }

        if (page === "ifta") {
            if (!hasProduct("ELD")) return false;

            if (slug === "fuel-receipts") {
                return !hideFuelReceipts && hasPermission(perm);
            }
            if (slug === "reports") {
                return hasIFTAReports && hasPermission(perm);
            }
            return hasPermission(perm);
        }

        if (page === "mobile-worker") {
            if (!hasProduct("WORK_ORDER")) return false;

            if (slug === "mobile-worker-reports") {
                return hasWorkOrderReports && hasPermission(perm);
            }
            return hasPermission(perm);
        }

        if (page === "dvir") {
            if (!hasProduct("DVIR")) return false;
            return hasPermission(perm);
        }

        if (page === "admin-tools") {
            return hasPermission(perm);
        }

        return hasPermission(perm);
    }
    return false;
}
});
